import CommonAccordion from "component/Common/Accordion";
import CommonModal from "component/Common/CommonModal";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  HeartIcon as ChoiceListHeartIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon } from "@heroicons/react/24/solid";
import { svgIcons } from "helpers/images";
import { CircularSpinner } from "helpers/muiComponents";
import { endPoints } from "service/endpoints";
import { request } from "service/request";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import useOnClickOutside from "helpers/useOnClickOutside";

const ChoiceListContainer = ({ handleSelectionContainerClose }) => {
  const location = useLocation();
  const [choiceListSearch, setChoiceListSearch] = useState("");
  const choiceListSearchRef = useRef();
  const createChoiceListRef = useRef(null);
  const deleteChoiceListRef = useRef(null);
  const counsellingSearchRef = useRef();

  const [myChoiceLists, setMyChoiceLists] = useState([]);
  const [counsellingOptions, setCounsellingOptions] = useState([]);
  const [removeChoiceList, setRemoveChoiceList] = useState({});
  const [showDeleteChoiceList, setShowDeleteChoiceList] = useState(false);
  const [showCreateChoiceList, setShowCreateChoiceList] = useState(false);
  const [deletingCounselling, setDeletingCounselling] = useState({});
  const [choiceListCounselling, setChoiceListCounselling] = useState(null);
  const [counsellingSearch, setCounsellingSearch] = useState("");
  const [newChoiceListName, setNewChoiceListName] = useState("");
  const [choiceListNameError, setChoiceListNameError] = useState(null);
  const [isCreateChoiceListApiLoading, setIsCreateChoiceListApiLoading] =
    useState(false);
  const [isChoiceListApiLoading, setIsChoiceListApiLoading] = useState(false);

  const getMyChoiceLists = async () => {
    setIsChoiceListApiLoading(true);
    try {
      const response = await request({
        url: `${endPoints.choiceList.choicelistTable}`,
        method: "GET",
        location: location,
      });
      if (response?.success) {
        setMyChoiceLists(response?.data?.choice_lists);
        setIsChoiceListApiLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getCounsellingList = async () => {
    setIsCreateChoiceListApiLoading(true);
    try {
      const counsellings = await request({
        url: `${endPoints.choiceList.counsellings}`,
        method: "GET",
        location: location,
      });

      if (counsellings?.data?.counsellings) {
        const CounsellingsOptions = counsellings?.data?.counsellings?.map(
          (item) => ({
            label: item.name,
            value: item.id,
          }),
        );
        setCounsellingOptions(CounsellingsOptions);
        setIsCreateChoiceListApiLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const createChoiceListHandler = async () => {
    const params = {
      counselling_id: choiceListCounselling?.value,
      name: newChoiceListName,
    };
    try {
      const response = await request({
        url: `${endPoints.choiceList.createChoicelist}`,
        method: "POST",
        data: params,
        showToast: true,
        location: location,
      });
      if (response.success === true) {
        setMyChoiceLists((prevList) => [
          ...prevList,
          response?.data?.choice_list,
        ]);
        window.open(
          `choiceTable?counselling_id=${response?.data?.choice_list?.counselling?.id}&choice_list_id=${response?.data?.choice_list?.id}`,
          "_self",
        );
      }
    } catch (error) {
      console.error("Error Add Choice List:", error);
      setChoiceListNameError(error);
    }
  };

  const removeChoiceListHandler = async () => {
    try {
      const response = await request({
        url: `${endPoints.choiceList.createChoicelist}/${removeChoiceList?.id}`,
        method: "DELETE",
        showToast: true,
        location: location,
      });
      if (response.success === true) {
        getMyChoiceLists();
      }
    } catch (error) {
      console.error("Error Remove Choice List:", error);
    }
    setShowDeleteChoiceList(false);
  };

  const structuredChoiceLists = () => {
    if (!myChoiceLists || myChoiceLists.length === 0) {
      return {};
    }
    const searchQuery = choiceListSearch?.toLowerCase() || "";

    return myChoiceLists
      .filter((list) => {
        return (
          !searchQuery ||
          list.counselling.name.toLowerCase().includes(searchQuery) ||
          list.name.toLowerCase().includes(searchQuery)
        );
      })
      .reduce((acc, list) => {
        const counsellingName = list.counselling.name;
        if (!acc[counsellingName]) {
          acc[counsellingName] = [];
        }
        acc[counsellingName].push(list);

        return acc;
      }, {});
  };

  const searchCounsellings = () => {
    if (!counsellingOptions || counsellingOptions?.length === 0) {
      return [];
    }
    const searchQuery = counsellingSearch?.toLowerCase() || "";
    return counsellingOptions.filter((counselling) => {
      return (
        !searchQuery || counselling.label.toLowerCase().includes(searchQuery)
      );
    });
  };

  const handleCreateChoiceListClose = () => {
    setShowCreateChoiceList(false);
    setChoiceListCounselling(null);
    setCounsellingSearch("");
    setNewChoiceListName("");
    if (choiceListNameError) setChoiceListNameError(null);
  };

  const handleCreateChoiceListOpen = () => {
    getCounsellingList();
    setShowCreateChoiceList(true);
  };

  const enableCreateChoiceListButton = () => {
    return (
      choiceListCounselling?.value &&
      newChoiceListName !== "" &&
      !choiceListNameError
    );
  };

  useEffect(() => {
    getMyChoiceLists();
  }, []);

  useOnClickOutside(deleteChoiceListRef, () => {
    setShowDeleteChoiceList(false);
  });

  useOnClickOutside(createChoiceListRef, () => {
    handleCreateChoiceListClose();
  });

  useEffect(() => {
    getMyChoiceLists();
  }, []);

  return (
    <div className="space-y-4">
      <div id="selectionContainerTitle" className="flex justify-between">
        <p className="text-xl font-bold">My Choice Lists</p>
        <span className="flex items-center  justify-between gap-3">
          <XMarkIcon
            className="w-4 h-4 aspect-square stroke-[3px] cursor-pointer"
            onClick={() => handleSelectionContainerClose()}
          />
        </span>
      </div>
      <div className="space-y-2">
        <div
          className={`relative flex items-center justify-between w-full min-h-9 max-h-9 bg-grey-50 rounded-lg overflow-hidden`}
        >
          <input
            ref={choiceListSearchRef}
            onChange={(e) => {
              setChoiceListSearch(e.target.value);
            }}
            type="text"
            placeholder="Search your Choice Lists"
            value={choiceListSearch}
            className="w-full text-grey-600 bg-grey-50 text-xs font-semibold py-2 pl-10 pr-3 focus-visible:outline-0 focus:!outline-offset-0"
            autoFocus
          />
          <MagnifyingGlassIcon className="absolute left-3 w-4 h-4 object-cover" />
        </div>
        <div className="flex flex-col overflow-auto">
          <div className="flex flex-col justify-between items-center w-full">
            {myChoiceLists.length > 0 ? (
              Object.keys(structuredChoiceLists()).length > 0 ? (
                <div className="w-full space-y-2">
                  {Object.entries(structuredChoiceLists()).map(
                    ([counselling, choicelists], index) => (
                      <CommonAccordion
                        key={index}
                        accordionClass={{
                          boxShadow: "none",
                          padding: "4px",
                          borderRadius: "8px !important",
                          backgroundColor: "#F5F5F5",
                          "&:hover": {
                            backgroundColor: "#F0F0F0",
                          },
                          "&::before": { display: "none" },
                          "&.Mui-expanded": {
                            margin: "0px",
                          },
                        }}
                        summaryClass={{
                          alignItems: "center",
                          padding: "4px",
                          minHeight: "0px",
                          "& .MuiAccordionSummary-content": {
                            margin: "0px !important",
                          },
                          "&.Mui-expanded": {
                            minHeight: "0px",
                          },
                        }}
                        detailsClass={{
                          padding: "0px",
                        }}
                        summaryTypographyClass={{
                          fontWeight: "500",
                          fontSize: "12px",
                          color: "#5E5E5E",
                        }}
                        detailTypographyClass={{
                          fontWeight: "500",
                          fontSize: "12px",
                          color: "#5E5E5E",
                        }}
                        title={
                          <div className="flex !gap-3 w-full h-full items-start justify-start">
                            <svgIcons.counselling
                              className={`w-5 my-1 text-black`}
                            />
                            <div className="flex flex-col">
                              <p className="font-medium text-sm text-black">
                                {counselling}
                              </p>
                              <p className="font-medium text-xs text-black">
                                {choicelists.length} choice lists
                              </p>
                            </div>
                          </div>
                        }
                        children={
                          <div className="bg-white w-full p-1 rounded-md">
                            {choicelists?.map((list, index) => {
                              return (
                                <div
                                  key={index}
                                  className="flex w-full items-start justify-between !gap-3 !p-3 rounded hover:bg-grey-25"
                                  onClick={handleSelectionContainerClose}
                                >
                                  <Link
                                    to={`choiceTable?counselling_id=${list.counselling.id}&choice_list_id=${list.id}`}
                                    className="flex items-start gap-2 w-full"
                                    onClick={() => {
                                      setChoiceListSearch("");
                                    }}
                                  >
                                    <div className="relative mt-[2px]">
                                      <ChoiceListHeartIcon className="w-6 h-6 text-black absolute" />
                                      <HeartIcon
                                        className="w-6 h-6"
                                        color="#FFACAC"
                                      />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-black">
                                        {list.name}
                                      </p>
                                      <p className="text-xs text-grey-700 font-medium leading-normal">
                                        {list.total_choices} Choices
                                      </p>
                                    </div>
                                  </Link>
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowDeleteChoiceList(true);
                                      setDeletingCounselling(list.counselling);
                                      setRemoveChoiceList({
                                        id: list.id,
                                        name: list.name,
                                      });
                                    }}
                                    className="flex items-center justify-center bg-red-50 rounded p-2 cursor-pointer"
                                  >
                                    <TrashIcon className="w-4 h-4 object-cover text-red stroke-2" />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        }
                        showSeparator={false}
                        expand={false}
                      />
                    ),
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-2 text-center font-satoshi mx-10 my-10">
                  <p className="text-grey-700 font-semibold text-lg">
                    No choice lists found
                  </p>
                </div>
              )
            ) : !isChoiceListApiLoading ? (
              <div className="flex flex-col gap-4 text-center font-satoshi mx-10 my-10">
                <div className="font-bold">
                  <p>
                    Create Counseling specific Choice lists to curate your
                    choices (preferred order of seats) specific to each
                    counseling.
                  </p>
                  <p>
                    You can add to a preferred seat to a Choice list from the
                    Allotments, Closing Ranks, Seat Matrix, and Fee, Stipend &
                    Bond menus.
                  </p>
                </div>
                <div className="text-grey-700">
                  <p>
                    You don't have any choice lists currently. Create one here
                    or add from any of the sections.
                  </p>
                </div>
              </div>
            ) : (
              isChoiceListApiLoading && <CircularSpinner />
            )}

            <div
              onClick={() => handleCreateChoiceListOpen()}
              className="w-full px-3 py-2 mx-6 mt-6 cursor-pointer bg-primary hover:bg-primary-dark text-white flex items-center justify-center gap-2 rounded text-sm font-bold"
            >
              <div>
                <PlusIcon className="w-4 h-4 stroke-2" />
              </div>
              <div>Create Choice List</div>
            </div>
          </div>
        </div>
      </div>
      <CommonModal
        CommonModal
        show={showCreateChoiceList}
        center={true}
        handleClose={handleCreateChoiceListClose}
        size="md"
      >
        <div
          ref={createChoiceListRef}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="flex justify-between items-center px-4 py-3 shadow-[0_4px_24px_0px_rgba(0,0,0,0.05)]">
            <div className="text-black text-sm font-black">
              Create Choice List
            </div>
            <XMarkIcon
              className="w-6 h-6 stroke-2 cursor-pointer"
              onClick={handleCreateChoiceListClose}
            />
          </div>
          <div className="py-4 px-4 h-[420px]">
            {!choiceListCounselling?.value ? (
              <div className="flex flex-col gap-3 h-full">
                <p className="text-sm font-bold">
                  Please select a counselling to continue
                </p>
                <div className="flex flex-col gap-2 pb-3 h-full">
                  <div
                    className={`relative min-h-9 max-h-9 bg-grey-50 rounded overflow-hidden flex items-center justify-between`}
                  >
                    <input
                      ref={counsellingSearchRef}
                      onChange={(e) => {
                        setCounsellingSearch(e.target.value);
                      }}
                      type="text"
                      placeholder="Search Counselling"
                      value={counsellingSearch}
                      className="w-full text-grey-600 bg-grey-50 text-xs font-semibold py-2 pl-10 pr-3 focus-visible:outline-0 focus:!outline-offset-0"
                      autoFocus
                    />
                    <MagnifyingGlassIcon className="absolute left-3 w-4 h-4 object-cover" />
                  </div>
                  <div className="h-full overflow-y-auto">
                    {!isCreateChoiceListApiLoading ? (
                      searchCounsellings()?.map((counselling, index) => {
                        return (
                          <div
                            key={index}
                            className="flex !gap-3 items-center justify-between p-3 hover:bg-grey-50 cursor-pointer"
                            onClick={() => {
                              setChoiceListCounselling(counselling);
                            }}
                          >
                            <div className="flex items-center justify-center !gap-3">
                              <svgIcons.counselling
                                className={`w-5 text-black`}
                              />
                              <div className="flex flex-col">
                                <p className="font-bold text-sm text-black">
                                  {counselling?.label}
                                </p>
                              </div>
                            </div>
                            <ChevronRightIcon className="min-w-3 min-h-3 max-w-3 max-h-3 object-cover" />
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex items-center justify-center">
                        {isCreateChoiceListApiLoading && <CircularSpinner />}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col !gap-3 h-full">
                <p
                  className="flex gap-1 text-sm font-bold justify-start items-center text-primary cursor-pointer"
                  onClick={() => {
                    setChoiceListCounselling(null);
                    setNewChoiceListName("");
                    setChoiceListNameError(null);
                  }}
                >
                  <span>
                    <ChevronLeftIcon className="min-w-3 min-h-3 h-3 w-3 stroke-[3px] object-cover" />
                  </span>
                  {choiceListCounselling?.label}
                </p>
                <div className="flex flex-col !gap-2">
                  <p className="text-sm font-bold">
                    Give your choice list a name
                  </p>
                  <input
                    type="text"
                    className={`w-full min-h-10 px-3 text-sm font-medium border ${
                      choiceListNameError && choiceListNameError?.message
                        ? "!border-red"
                        : "!border-grey-border"
                    } focus:outline-0 rounded`}
                    placeholder="Enter list name"
                    value={newChoiceListName}
                    onChange={(e) => {
                      setNewChoiceListName(e.target.value);
                      if (choiceListNameError) setChoiceListNameError(null);
                    }}
                  />
                </div>
                {choiceListNameError && (
                  <div className=" flex !gap-2 p-3 bg-red-500 items-start w-full text-xs font-bold text-red">
                    <InformationCircleIcon className="min-w-4 min-h-4 w-4 h-4 stroke-2" />
                    <span>{choiceListNameError?.message}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="text-xs font-bold flex justify-end gap-2 items-center p-3 shadow-[0_-4px_24px_0px_rgba(0,0,0,0.05)]">
            <button
              onClick={handleCreateChoiceListClose}
              className="px-6 py-2 bg-grey-50 rounded cursor-pointer"
            >
              Cancel
            </button>
            <button
              className={`flex items-center gap-2 pl-4 pr-6 py-2 ${
                enableCreateChoiceListButton()
                  ? "bg-primary text-white cursor-pointer"
                  : "bg-grey-50 text-grey-600 cursor-not-allowed"
              } rounded`}
              onClick={() => createChoiceListHandler()}
            >
              <PlusIcon className="w-4 h-4 stroke-2" />
              Create List
            </button>
          </div>
        </div>
      </CommonModal>
      <CommonModal
        size="md"
        customBodyClass="!p-0 rounded-lg overflow-hidden"
        show={showDeleteChoiceList}
        handleClose={() => {
          setShowDeleteChoiceList(false);
        }}
        counsellingName={deletingCounselling?.name}
        center="true"
      >
        <div ref={deleteChoiceListRef}>
          <div className="flex items-center justify-between pop-header p-4">
            <p className="text-sm font-black">Delete Choice List</p>
            <XMarkIcon
              className="w-6 stroke-2 cursor-pointer"
              onClick={() => setShowDeleteChoiceList(false)}
            />
          </div>
          <div className="flex flex-col gap-3 items-center justify-center m-6 p-3">
            <div className="font-normal text-sm leading-normal text-center">
              Are you sure you want to delete
              <span className="font-bold mx-1">{removeChoiceList?.name}</span>
              from your
              <span className="font-bold mx-1">
                {deletingCounselling?.name}
              </span>
              ?
            </div>
            <div className="font-normal text-sm leading-normal text-center">
              Any choices you've added to this list will also be lost.
            </div>
          </div>
          <div className="text-xs font-bold flex justify-end gap-2 items-center p-3 shadow-[0_-4px_24px_0px_rgba(0,0,0,0.10)]">
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteChoiceList(false);
              }}
              className="px-6 py-2 border !border-grey-border rounded cursor-pointer"
            >
              Cancel
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                removeChoiceListHandler();
              }}
              className="flex items-center gap-2 px-6 py-2 border !border-red bg-red-500 text-red rounded cursor-pointer"
            >
              <div>
                <TrashIcon className="w-4 object-cover text-red stroke-2" />
              </div>
              <p>Delete List</p>
            </div>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default ChoiceListContainer;
